import React, { useState, useEffect } from 'react';
import { doc, getDoc, updateDoc } from 'firebase/firestore';
import { useParams, useNavigate } from 'react-router-dom';
import moment from 'moment';
import { db } from '../../firebase'; // Import Firebase configuration
import './transaction.css';

const TransactionLog = () => {
    // * Get userId from URL parameters
    const { userId } = useParams();
    const navigate = useNavigate();
    const [transactions, setTransactions] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [balance, setBalance] = useState(0);
    const [userName, setUserName] = useState(''); // State to store the user's name
    const [selectedTransaction, setSelectedTransaction] = useState(null); // State to manage the transaction being viewed or edited

    const [summary, setSummary] = useState({
        savings: 0,
        investments: 0,
        loans: 0,
        allDebits: 0,
        allCredits: 0,
        totalTransactions: 0,
    });

    useEffect(() => {
        // * Function to fetch transactions from Firestore
        const fetchTransactions = async () => {
            try {
                const userDoc = await getDoc(doc(db, 'users', userId));
                if (userDoc.exists()) {
                    const userData = userDoc.data();
                    let fetchedTransactions = userData.transactions || [];
    
                    // Sort transactions by date (latest to earliest) using moment for date comparison
                    fetchedTransactions = fetchedTransactions.sort((a, b) => moment(b.date).diff(moment(a.date)));
                    setTransactions(fetchedTransactions);  // Set the transactions in the correct order
    
                    setUserName(userData.fullName); // Set the user's name
    
                    // Calculate the balance and summary
                    const calculatedBalance = fetchedTransactions.reduce((total, transaction) => {
                        return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
                    }, 0);
                    setBalance(calculatedBalance);
    
                    const summary = fetchedTransactions.reduce(
                        (acc, transaction) => {
                            acc.totalTransactions += 1;
                            const amount = transaction.type === 'debit' ? -transaction.amount : transaction.amount;
                            if (transaction.type === 'credit') {
                                acc.allCredits += transaction.amount;
                            } else {
                                acc.allDebits += transaction.amount;
                            }
                            if (transaction.account === 'Savings') {
                                acc.savings += amount;
                            } else if (transaction.account === 'Investments') {
                                acc.investments += amount;
                            } else if (transaction.account === 'Loans') {
                                acc.loans += amount;
                            }
                            return acc;
                        },
                        { savings: 0, investments: 0, loans: 0, allDebits: 0, allCredits: 0, totalTransactions: 0 }
                    );
                    setSummary(summary);
                } else {
                    setError('User not found.');
                }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching transactions: ', error);
                setError('Error fetching transactions.');
                setLoading(false);
            }
        };
    
        fetchTransactions();
    }, [userId]); // !Fetch transactions whenever userId changes

    // Handle click on the view button
    const handleViewClick = (index) => {
        setSelectedTransaction(transactions[index]);
    };

    // Handle changes in the edit form
    const handleEditChange = (e) => {
        const { name, value } = e.target;
        setSelectedTransaction((prev) => ({
            ...prev,
            [name]: name === 'amount' ? parseFloat(value) : value,
        }));
    };

    // Save the edited transaction
    const handleEditSave = async () => {
        const updatedTransactions = [...transactions];
        const index = transactions.findIndex(t => t.date === selectedTransaction.date && t.amount === selectedTransaction.amount);
        updatedTransactions[index] = selectedTransaction;

        try {
            await updateDoc(doc(db, 'users', userId), {
                transactions: updatedTransactions,
            });
            // Sort transactions by date (latest to earliest)
            setTransactions(updatedTransactions.sort((a, b) => moment(b.date).diff(moment(a.date))));

            // Recalculate the balance
            const calculatedBalance = updatedTransactions.reduce((total, transaction) => {
                return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
            }, 0);
            setBalance(calculatedBalance);

            const summary = updatedTransactions.reduce(
                (acc, transaction) => {
                    acc.totalTransactions += 1;
                    const amount = transaction.type === 'debit' ? -transaction.amount : transaction.amount;
                    if (transaction.type === 'credit') {
                        acc.allCredits += transaction.amount;
                    } else {
                        acc.allDebits += transaction.amount;
                    }
                    if (transaction.account === 'Savings') {
                        acc.savings += amount;
                    } else if (transaction.account === 'Investments') {
                        acc.investments += amount;
                    } else if (transaction.account === 'Loans') {
                        acc.loans += amount;
                    }
                    return acc;
                },
                { savings: 0, investments: 0, loans: 0, allDebits: 0, allCredits: 0, totalTransactions: 0 }
            );
            setSummary(summary);

            setSelectedTransaction(null);
        } catch (error) {
            console.error('Error updating transaction: ', error);
        }
    };

    // Cancel the edit action
    const handleCancelEdit = () => {
        setSelectedTransaction(null);
    };

    // Handle the delete action
    const handleDeleteClick = async () => {
        const updatedTransactions = transactions.filter((t) => t !== selectedTransaction);

        try {
            await updateDoc(doc(db, 'users', userId), {
                transactions: updatedTransactions,
            });
            // Sort transactions by date (latest to earliest)
            setTransactions(updatedTransactions.sort((a, b) => moment(b.date).diff(moment(a.date))));

            // Recalculate the balance
            const calculatedBalance = updatedTransactions.reduce((total, transaction) => {
                return transaction.type === 'credit' ? total + transaction.amount : total - transaction.amount;
            }, 0);
            setBalance(calculatedBalance);

            const summary = updatedTransactions.reduce(
                (acc, transaction) => {
                    acc.totalTransactions += 1;
                    const amount = transaction.type === 'debit' ? -transaction.amount : transaction.amount;
                    if (transaction.type === 'credit') {
                        acc.allCredits += transaction.amount;
                    } else {
                        acc.allDebits += transaction.amount;
                    }
                    if (transaction.account === 'Savings') {
                        acc.savings += amount;
                    } else if (transaction.account === 'Investments') {
                        acc.investments += amount;
                    } else if (transaction.account === 'Loans') {
                        acc.loans += amount;
                    }
                    return acc;
                },
                { savings: 0, investments: 0, loans: 0, allDebits: 0, allCredits: 0, totalTransactions: 0 }
            );
            setSummary(summary);
            setSelectedTransaction(null);
        } catch (error) {
            console.error('Error deleting transaction: ', error);
        }
    };

    // Navigate back to the previous page
    const handleGoBack = () => {
        navigate(-1);
    };

    if (loading) {
        return <div>Loading...</div>;
    }

    if (error) {
        return <div>{error}</div>;
    }

    if (transactions.length === 0) {
        return <div className='transaction-log'>No transactions found.</div>;
    }

    return (
        <div className="transaction-log">
            {/* Display the user's name */}
            <h2>Transaction Log for {userName}</h2>
            {/* Display account balance */}
            <h5>Total Balance: ${balance.toFixed(2)}</h5>

            {/* display summary of all transactions and balances */}
            <div className="summary-boxes">
                <div className="summary-box">
                    <h3>Savings</h3>
                    <p>${summary.savings.toFixed(2)}</p>
                </div>
                <div className="summary-box">
                    <h3>Investments</h3>
                    <p>${summary.investments.toFixed(2)}</p>
                </div>
                <div className="summary-box">
                    <h3>Loans</h3>
                    <p>${summary.loans.toFixed(2)}</p>
                </div>
                <div className="summary-box">
                    <h3>All Debits</h3>
                    <p>${summary.allDebits.toFixed(2)}</p>
                </div>
                <div className="summary-box">
                    <h3>All Credits</h3>
                    <p>${summary.allCredits.toFixed(2)}</p>
                </div>
                <div className="summary-box">
                    <h3>Total Transactions</h3>
                    <p>{summary.totalTransactions}</p>
                </div>
            </div>


            {/* Time for transaction history */}
            <table>
                <thead>
                    <tr>
                        {/* Display the head columns for transaction info */}
                        <th>Amount</th>
                        <th>Description</th>
                        <th>Transaction</th>
                        <th>Date</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {transactions.map((transaction, index) => (
                        <tr key={index}>
                            {/* display the main values of the transaction */}
                            <td>${transaction.amount.toFixed(2)}</td>
                            <td>{transaction.description}</td>
                            <td>{transaction.type || 'N/A'}</td>
                            <td>{moment(transaction.date).format('MMMM D, YYYY')}</td>
                            <td className='td'>
                                {/* View button to trigger the sidebar */}
                                <button onClick={() => handleViewClick(index)} className='main-button'>View</button>
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>

            {selectedTransaction && (
                // * display content for selected transaction
                <div className="sidebar">
                    <div className="sidebar-content">
                        {/* close button */}
                        <span className="close-button" onClick={handleCancelEdit}>
                            &times;
                        </span>

                        {/* View and Edit Amount */}
                        <div className='logTitle'>
                            ${selectedTransaction.amount} {selectedTransaction.type}ed on {moment(selectedTransaction.date).format('MMMM D, YYYY')}
                        </div>

                        {/* View and Edit Description */}
                        <div>
                            <label>Description:</label>
                            <input
                                type="text"
                                name="description"
                                value={selectedTransaction.description}
                                onChange={handleEditChange}
                            />
                        </div>

                        {/* View and Edit Beneficiary */}
                        <div>
                            <label>Beneficiary:</label>
                            <input
                                type="text"
                                name="beneficiary"
                                value={selectedTransaction.beneficiary || ''}
                                onChange={handleEditChange}
                            />
                        </div>

                        {/* View and Edit Type */}
                        <div>
                            <label>Type:</label>
                            <select
                                name="type"
                                value={selectedTransaction.type}
                                onChange={handleEditChange}
                            >
                                <option value="credit">Credit</option>
                                <option value="debit">Debit</option>
                            </select>
                        </div>

                        {/* View and Edit Account */}
                        <div>
                            <label>Account:</label>
                            <select
                                name="account"
                                value={selectedTransaction.account}
                                onChange={handleEditChange}
                            >
                                <option value="Savings">Savings</option>
                                <option value="Investments">Investments</option>
                                <option value="Loans">Loans</option>
                            </select>
                        </div>

                        {/* action buttons */}
                        <div className="sidebar-actions">
                            <button onClick={handleEditSave} className="main-button logout-button">Save</button>
                            <button onClick={handleDeleteClick} className="main-button logout-button">Delete</button>
                            <button onClick={handleCancelEdit} className="main-button">Cancel</button>
                        </div>
                    </div>
                </div>
            )}

            <div className="center">
                <button onClick={handleGoBack} className='button'>Go Back</button> {/* Go Back button */}
            </div>
        </div>
    );
};

export default TransactionLog;
